import {
  createContext,
  useEffect,
  useState,
  useContext,
  Dispatch,
  SetStateAction,
  useMemo,
  useRef,
} from 'react'
import { useMeeting } from '@videosdk.live/react-sdk'
import { HallUser, SpeakingRole, User } from '@db8/shared'
import { useHallUsersPubSub } from '@/hooks/pub-sub/use-hall-users-pub-sub'
import { useUser } from '@clerk/clerk-react'
import { useHall } from './HallProvider'
import { useTurns } from './TurnsProvider'
import { useAxios } from './AxiosProvider'
import { useSocket, useSocketContext } from '@/contexts/SocketProvider'

export type HallUsersContext = {
  hallUsers?: HallUser[] | null
  updateHallUsers: Dispatch<SetStateAction<HallUser[]>>
  updateHallUser: ((id: string, updatedUser: Partial<HallUser>) => void) | null
  ownerId: string | null
  getUser: (x: string) => HallUser | undefined
  getParticipant: (x: string) => HallUser | undefined
  isModerator: boolean
  isDebator: boolean
  isSpectator: boolean
  currentUser: HallUser | null
  addBot2: () => void
  affirmativeUsers: string[]
  negativeUsers: string[]
  moderatorUsers: string[]
  spectatorUsers: string[]
  spectatorUsersSansBot: HallUser[]
  moderatorUsersWithOwner: string[]
  botUsers: string[]
  teamMembers: string[]
  isBot: (user: HallUser) => boolean
  resetHallUsers: () => void
  dbUser: User | null
  removePinnedHallUsers: (ids: string[]) => string[]
  removeUserById: (id: string) => void
}

export const HallUsersContext = createContext<HallUsersContext>({
  hallUsers: null,
  updateHallUsers: null,
  updateHallUser: null,
  ownerId: null,
  getUser: null,
  getParticipant: null,
  isModerator: false,
  isDebator: false,
  isSpectator: false,
  isBot: () => false,
  currentUser: null,
  addBot2: () => {},
  affirmativeUsers: [],
  negativeUsers: [],
  moderatorUsers: [],
  spectatorUsers: [],
  teamMembers: [],
  botUsers: [],
  spectatorUsersSansBot: [],
  moderatorUsersWithOwner: [],
  resetHallUsers: () => {},
  dbUser: null,
  removePinnedHallUsers: (ids: string[]) => ids,
  removeUserById: () => {},
})

export function HallUsersProvider({
  children,
  ownerId,
  hallId,
}: {
  children?: React.ReactNode
  ownerId: string
  hallId: string
}) {
  const [stateHallUsers, setStateHallUsers] = useState<HallUser[]>([])
  const { publishHallUsers, hasSyncedOnce } = useHallUsersPubSub({
    hallUsers: stateHallUsers,
    setHallUsers: setStateHallUsers,
  })

  // Optimistic role pins: id -> desired role
  const [pinnedRole, setPinnedRole] = useState<Record<string, SpeakingRole>>({})

  // Tombstones to block server "zombie" echoes
  const [removedUserIds, setRemovedUserIds] = useState<Set<string>>(new Set())
  const [removedBotIds, setRemovedBotIds] = useState<Set<number>>(new Set()) // by botId
  const [removedBotClerks, setRemovedBotClerks] = useState<Set<string>>(new Set()) // by clerkId (e.g., "bot-3")

  const { pinnedParticipants } = useTurns()
  const { participants } = useMeeting()
  const { user: clerkUser } = useUser()
  const { isOwner, hall, setHallMetadata } = useHall()
  const [dbUser, setDBUser] = useState<User | null>(null)
  const axios = useAxios()
  const socket = useSocketContext()

  // Expose hallUsers with pinned roles applied
  const hallUsers = useMemo(
    () =>
      stateHallUsers.map((u) =>
        pinnedRole[u.id] != null ? { ...u, speakingRole: pinnedRole[u.id] } : u,
      ),
    [stateHallUsers, pinnedRole],
  )

  // Removed auto-seed bots behavior; bots will only be added explicitly

  const currentUser = useMemo(() => {
    if (!hallUsers || !clerkUser) return null
    return hallUsers.find((user) => user.clerkId === clerkUser.id) || null
  }, [hallUsers, clerkUser?.id])

  // Persist current user's role
  useEffect(() => {
    if (currentUser?.speakingRole != null && hall?.id) {
      localStorage.setItem(
        `speakingRole-${currentUser.clerkId}-${hall.id}`,
        String(currentUser.speakingRole),
      )
    }
  }, [currentUser?.clerkId, currentUser?.speakingRole, hall?.id])

  // --- publish helper (light debounce) ---
  const publishTimerRef = useRef<any>(null)
  const safePublish = (users: HallUser[]) => {
    if (publishTimerRef.current) clearTimeout(publishTimerRef.current)
    publishTimerRef.current = setTimeout(() => {
      publishHallUsers(users)
      publishTimerRef.current = null
    }, 120)
  }

  // ---- mutations ----
  const updateHallUser = (id: string, updatedUser: Partial<HallUser>) => {
    setStateHallUsers((prev) => {
      const next = prev.map((u) => (u.id === id ? { ...u, ...updatedUser } : u))
      if (updatedUser.speakingRole != null) {
        setPinnedRole((p) => ({ ...p, [id]: updatedUser.speakingRole as SpeakingRole }))
      }
      safePublish(next)
      return next
    })
  }

  const updateHallUsers = (updatedUsers: SetStateAction<HallUser[]>) => {
    setStateHallUsers((prev) => {
      const next =
        typeof updatedUsers === 'function'
          ? (updatedUsers as (p: HallUser[]) => HallUser[])(prev)
          : updatedUsers
      safePublish(next)
      return next
    })
  }

  const buildHallUserFromParticipant = (participant: any): HallUser | null => {
    // @ts-ignore
    const clerkUserId = participant?.metaData?.clerkUserId ?? participant?.clerkId
    if (!clerkUserId) return null
    // @ts-ignore
    const hallUserId = participant?.metaData?.hallUserId ?? clerkUserId

    let speakingRole: SpeakingRole = SpeakingRole.None
    if (hall?.id && clerkUser?.id && clerkUserId === clerkUser.id) {
      const stored = localStorage.getItem(`speakingRole-${clerkUserId}-${hall.id}`)
      if (
        stored === SpeakingRole.Moderator ||
        stored === SpeakingRole.Affirmative ||
        stored === SpeakingRole.Negative ||
        stored === SpeakingRole.None
      ) {
        speakingRole = stored as SpeakingRole
      }
    }

    return {
      id: hallUserId,
      displayName: participant?.metaData?.displayName || participant?.displayName || 'Anonymous',
      imageUrl: participant?.metaData?.imageUrl ?? participant?.imageUrl,
      clerkId: clerkUserId,
      videoSDKId: participant.id,
      speakingRole,
    }
  }

  // Ensure local human exists & video id stays fresh
  useEffect(() => {
    if (!clerkUser || !participants) return
    const me = [...participants.values()].find(
      // @ts-ignore
      (p) => p?.metaData?.clerkUserId === clerkUser.id,
    )
    if (!me) return
    setStateHallUsers((prev) => {
      const exists = prev.find((u) => u.clerkId === clerkUser.id)
      if (!exists) {
        const newUser = buildHallUserFromParticipant(me)
        if (!newUser) return prev
        const next = [...prev, newUser]
        safePublish(next)
        return next
      }
      if (exists.videoSDKId !== me.id) {
        const next = prev.map((u) => (u.clerkId === clerkUser.id ? { ...u, videoSDKId: me.id } : u))
        safePublish(next)
        return next
      }
      return prev
    })
  }, [clerkUser?.id, participants, hall?.id])

  // Add Bot (optimistic) then swap with server response. Also clear tombstones for that bot.
  const addBot2 = async () => {
    // exclude currently visible + tombstoned bots from the available pool
    const visibleBotIds = stateHallUsers
      .filter((h) => h.botId != null)
      .map((h) => h.botId as number)
    const blockedBotIds = new Set<number>([...visibleBotIds, ...removedBotIds])
    const usedCount = visibleBotIds.length
    if (usedCount >= 2) return

    const availableBotIds = Array.from({ length: 10 }, (_, i) => i + 1).filter(
      (id) => !blockedBotIds.has(id),
    )
    if (availableBotIds.length === 0) return

    const botId = availableBotIds[Math.floor(Math.random() * availableBotIds.length)]
    const clerk = `bot-${botId}`

    // optimistic temp bot
    const tempId = `temp-bot-${Date.now()}-${Math.random().toString(36).slice(2)}`
    const tempBot: HallUser = {
      id: tempId,
      botId,
      clerkId: clerk,
      displayName: `AI Bot ${botId}`,
      imageUrl: undefined,
      videoSDKId: clerk,
      speakingRole: SpeakingRole.None,
    }

    setStateHallUsers((prev) => {
      const next = [...prev, tempBot]
      safePublish(next)
      return next
    })

    try {
      const { data: newBot } = await axios.post(`/api/hall/${hall?.id}/add-bot`, { botId })
      // We intentionally clear tombstones for THIS bot, because we just re-added it.
      setRemovedUserIds((s) => {
        const next = new Set(s)
        next.delete(newBot.id)
        return next
      })
      setRemovedBotIds((s) => {
        const next = new Set(s)
        if (newBot.botId != null) next.delete(newBot.botId as number)
        return next
      })
      setRemovedBotClerks((s) => {
        const next = new Set(s)
        if (newBot.clerkId) next.delete(newBot.clerkId)
        return next
      })

      setStateHallUsers((prev) => {
        const idx = prev.findIndex((u) => u.id === tempId || u.botId === botId)
        if (idx === -1) return prev
        const next = [...prev]
        next[idx] = newBot
        safePublish(next)
        return next
      })
    } catch {
      // rollback temp on failure
      setStateHallUsers((prev) => {
        const next = prev.filter((u) => u.id !== tempId)
        safePublish(next)
        return next
      })
    }
  }

  // Remove (with tombstones). If it's a bot, mark all 3 keys so server echoes are ignored.
  const removeUserById = (id: string) => {
    setStateHallUsers((prev) => {
      const victim = prev.find((u) => u.id === id)
      const next = prev.filter((u) => u.id !== id)
      safePublish(next)

      if (victim?.botId != null) {
        setRemovedBotIds((s) => {
          const copy = new Set(s)
          copy.add(victim.botId as number)
          return copy
        })
      }
      if (victim?.clerkId) {
        setRemovedBotClerks((s) => {
          const copy = new Set(s)
          copy.add(victim.clerkId)
          return copy
        })
      }
      setRemovedUserIds((s) => {
        const copy = new Set(s)
        copy.add(id)
        return copy
      })

      return next
    })

    // OPTIONAL: persist deletion on backend (strongest fix; prevents server from ever echoing it)
    // axios.delete(`/api/hall/${hall?.id}/users/${id}`).catch(() => {
    //   // If delete fails server-side, we still keep the tombstones so it doesn't resurrect locally.
    // })
  }

  const removePinnedHallUsers = (ids: string[]) => {
    const set = new Set(pinnedParticipants)
    return ids.filter((id) => !set.has(id))
  }

  const isModerator = useMemo(() => {
    if (isOwner) return true
    if (!clerkUser) return false
    return !!hallUsers.find(
      (user) => user.clerkId === clerkUser.id && user.speakingRole === SpeakingRole.Moderator,
    )
  }, [isOwner, hallUsers, clerkUser?.id])

  const isDebator = useMemo(() => {
    if (!currentUser) return false
    return [SpeakingRole.Affirmative, SpeakingRole.Negative].includes(currentUser.speakingRole)
  }, [currentUser?.speakingRole])

  const getUser = (id: string) => hallUsers.find((x) => x?.id === id)
  const getParticipant = (id: string) => hallUsers.find((x) => x.videoSDKId === id)
  const isBot = (user: HallUser) => !!user?.botId

  // --- Grouped ids for consumers ---
  const groupedParticipants = useMemo(() => {
    const affirmativeUsers: string[] = []
    const negativeUsers: string[] = []
    const moderatorUsers: string[] = []
    const spectatorUsers: string[] = []
    const spectatorUsersSansBot: HallUser[] = []
    const moderatorUsersWithOwner: string[] = []
    const teamMembers: string[] = []
    const botUsers: string[] = []

    hallUsers?.forEach((user) => {
      if (!user?.id) return
      const id = user.id
      const userIsOwner = hall?.ownerId === user.clerkId

      if (user.speakingRole === SpeakingRole.None && !isBot(user)) spectatorUsersSansBot.push(user)
      if (userIsOwner) moderatorUsersWithOwner.push(id)
      if (user.botId) botUsers.push(user.id)

      switch (user.speakingRole) {
        case SpeakingRole.Affirmative:
          affirmativeUsers.push(id)
          teamMembers.push(id)
          break
        case SpeakingRole.Negative:
          negativeUsers.push(id)
          teamMembers.push(id)
          break
        case SpeakingRole.Moderator:
          moderatorUsers.push(id)
          moderatorUsersWithOwner.push(id)
          break
        default:
          spectatorUsers.push(id)
      }
    })

    pinnedParticipants?.forEach((id) => teamMembers.push(id))

    return {
      affirmativeUsers,
      negativeUsers,
      moderatorUsers,
      spectatorUsers,
      spectatorUsersSansBot,
      moderatorUsersWithOwner,
      teamMembers,
      botUsers,
    }
  }, [hallUsers, pinnedParticipants, hall])

  // --- Socket join/leave ---
  useEffect(() => {
    if (!socket || !hallId) return
    socket.emit('join-hall', { hallId })
    return () => {
      socket.emit('leave-hall', { hallId })
    }
  }, [socket, hallId])

  // --- Merge-only server sync; ignore tombstoned bots by ANY key ---
  useSocket({
    'hall-users-sync': (payload: { hallId: string; users: HallUser[] }) => {
      if (payload?.hallId !== hall?.id) return

      const participantsList = [...participants.values()]

      let remote = payload.users.filter((u) => {
        // Humans: keep only if present in the room
        if (!u.botId) {
          return participantsList.some((p: any) => p?.metaData?.clerkUserId === u.clerkId)
        }
        // Bots: keep unless tombstoned by ANY key
        if (removedUserIds.has(u.id)) return false
        if (u.botId != null && removedBotIds.has(u.botId as number)) return false
        if (u.clerkId && removedBotClerks.has(u.clerkId)) return false
        return true
      })

      const byId = new Map(remote.map((u) => [u.id, u]))

      setStateHallUsers((prev) => {
        const next = [...prev]

        for (const srv of remote) {
          // upsert by id
          const idxById = next.findIndex((u) => u.id === srv.id)
          if (idxById !== -1) {
            next[idxById] = { ...next[idxById], ...srv }
            continue
          }
          // or by clerkId (canonical replacement)
          if (srv.clerkId) {
            const idxByClerk = next.findIndex((u) => u.clerkId === srv.clerkId)
            if (idxByClerk !== -1) {
              const oldId = next[idxByClerk].id
              // carry pinned role to new id if present
              setPinnedRole((p) => {
                if (p[oldId] == null) return p
                const copy = { ...p }
                copy[srv.id] = copy[oldId]
                delete copy[oldId]
                return copy
              })
              next[idxByClerk] = srv
              continue
            }
          }
          next.push(srv)
        }

        // Do NOT delete local rows on sync.
        // Clear tombstones if server also stopped sending that id (optional but harmless)
        setRemovedUserIds((prevIds) => {
          if (!prevIds.size) return prevIds
          const still = new Set(prevIds)
          for (const id of prevIds) if (!byId.has(id)) still.delete(id)
          return still
        })

        return next
      })
    },
  })

  // --- Humans-only prune when they actually leave (never bots) ---
  useEffect(() => {
    if (!hasSyncedOnce) return
    const participantsList = [...participants.values()]
    setStateHallUsers((prev) => {
      const kept = prev.filter(
        (u) => u.botId || participantsList.some((p: any) => p?.metaData?.clerkUserId === u.clerkId),
      )
      return kept
    })
  }, [hasSyncedOnce, participants])

  // DB user (optional)
  useEffect(() => {
    const load = async () => {
      if (!clerkUser || dbUser) return
      try {
        const res = await axios.get(`/users/${clerkUser.id}`)
        setDBUser(res.data)
      } catch {}
    }
    load()
  }, [clerkUser, dbUser, axios])

  const {
    affirmativeUsers,
    negativeUsers,
    moderatorUsers,
    spectatorUsers,
    spectatorUsersSansBot,
    moderatorUsersWithOwner,
    teamMembers,
    botUsers,
  } = groupedParticipants

  const isSpectator = useMemo(
    () => (currentUser ? currentUser.speakingRole === SpeakingRole.None : false),
    [currentUser?.speakingRole],
  )

  const resetHallUsers = () => {
    const next = stateHallUsers.map((u) => ({ ...u, speakingRole: SpeakingRole.None }))
    setStateHallUsers(next)
    safePublish(next)
  }

  return (
    <HallUsersContext.Provider
      value={{
        hallUsers,
        updateHallUsers,
        updateHallUser,
        ownerId,
        getUser: (id: string) => hallUsers.find((x) => x?.id === id),
        getParticipant: (id: string) => hallUsers.find((x) => x.videoSDKId === id),
        isModerator,
        isDebator,
        currentUser,
        addBot2,
        affirmativeUsers,
        negativeUsers,
        moderatorUsers,
        spectatorUsers,
        spectatorUsersSansBot,
        moderatorUsersWithOwner,
        botUsers,
        teamMembers,
        isSpectator,
        isBot: (user: HallUser) => !!user?.botId,
        resetHallUsers,
        dbUser,
        removePinnedHallUsers,
        removeUserById,
      }}
    >
      {children}
    </HallUsersContext.Provider>
  )
}

export const useHallUsers = () => {
  const context = useContext(HallUsersContext)
  if (!context) throw new Error('hallUsers must be used within a HallUsersProvider')
  return context
}

export function useHallUser(participantId: string) {
  const { hallUsers } = useHallUsers()
  const participant = hallUsers?.find((user) => user.id === participantId)
  if (!participant) throw new Error(`User with ID ${participantId} not found in hallUsers.`)
  return participant
}
export const useHallUserCount = () => {
  const { hallUsers } = useHallUsers()
  return hallUsers?.length ?? 0
}
